/**
 * Contribution d'UN référent, lue à côté du consensus (§8.2).
 *
 * Même rendu que le référentiel agrégé (`QsortPyramide`) : c'est la comparaison
 * qui est recherchée, la retraduction mentale entre deux mises en page la
 * rendrait pénible. Chaque item porte donc l'attente du référent ET celle du
 * groupe, pas l'une puis l'autre.
 */
import { QsortPyramide, type ItemPyramide } from "@repo/results";
import { libelleZone } from "../../../../convex/referentiel/fourchette";
import { libelleAttentePourCible } from "./fourchetteLibelles";

type Dimension = { code: string; label: string };

export type StatQsort = { moyenne: number; ecartType: number; n: number };
export type StatFourchette = {
  min: number;
  max: number;
  ecartTypeMin: number;
  ecartTypeMax: number;
  n: number;
  poids?: number;
};

/** Agrégat de la version courante, quelle que soit l'échelle. */
export type Consensus = Record<string, StatQsort | StatFourchette>;

/** Échelle de saisie du référentiel, telle que portée par la config. */
export type EchelleReferentiel =
  | { kind: "qsort"; pyramide: number[] }
  | { kind: "fourchette"; min: number; max: number };

type CibleFourchette =
  | { min: number; max: number; poids?: number }
  | { nonConcerne: true };

type Props = {
  dimensions: Dimension[];
  /** `Record<code, score>` en Q-sort, `Record<code, cible>` en fourchette. */
  contribution: Record<string, unknown> | null;
  consensus: Consensus;
  echelle: EchelleReferentiel;
  /** Écart au consensus par dimension, tel que calculé côté serveur. */
  ecarts: { code: string; ecart: number }[];
  /** Contribution non validée : ce qui est montré peut encore bouger. */
  estBrouillon: boolean;
};

/** Écart d'au moins un cran complet : le référent ne classe pas cet item comme
 *  le groupe. Même seuil que la dispersion de `ReferentielPyramide`. */
const SEUIL_ECART = 1;

export function ReferentContribution({
  dimensions,
  contribution,
  consensus,
  echelle,
  ecarts,
  estBrouillon,
}: Props) {
  if (!contribution || Object.keys(contribution).length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Ce référent n'a encore rien saisi.
      </p>
    );
  }

  const libelle = (code: string) =>
    dimensions.find((d) => d.code === code)?.label ?? code;
  const ecartDe = (code: string) =>
    ecarts.find((e) => e.code === code)?.ecart ?? null;

  if (echelle.kind === "qsort") {
    const niveaux = echelle.pyramide.length;
    const scores = contribution as Record<string, number>;
    const items: ItemPyramide[] = Object.entries(scores).map(
      ([code, score]) => {
        const stat = consensus[code] as StatQsort | undefined;
        const ecart = ecartDe(code);
        return {
          code,
          label: libelle(code),
          // Inverse exact de `scoreDuNiveau` : score = N − niveau.
          niveau: Math.min(niveaux - 1, Math.max(0, niveaux - score)),
          valeur: String(score),
          detail: stat
            ? `groupe ${stat.moyenne.toFixed(1)}${
                ecart !== null ? ` · écart ${ecart.toFixed(1)}` : ""
              }`
            : "hors consensus",
          alerte: ecart !== null && ecart >= SEUIL_ECART,
        };
      },
    );

    return (
      <div className="space-y-3">
        {estBrouillon && (
          <p className="text-xs text-muted-foreground">
            Brouillon en cours : {items.length} item
            {items.length > 1 ? "s" : ""} placé{items.length > 1 ? "s" : ""}, la
            contribution n'est pas validée et n'entre pas dans le référentiel.
          </p>
        )}
        <QsortPyramide
          items={items}
          niveaux={niveaux}
          videMessage="Aucun item classé."
        />
      </div>
    );
  }

  // Pas d'écart au consensus ici : l'échelle fourchette est celle du PAMI, dont
  // le référent est UNIQUE — sa contribution EST le référentiel, l'écart vaudrait
  // 0 partout. L'ordre reste celui de la config, plus lisible qu'un tri par une
  // valeur constante.
  const cibles = contribution as Record<string, CibleFourchette>;
  const lignes = Object.entries(cibles).map(([code, cible]) => ({
    code,
    cible,
    stat: consensus[code] as StatFourchette | undefined,
  }));

  return (
    <div className="space-y-3">
      {estBrouillon && (
        <p className="text-xs text-muted-foreground">
          Brouillon en cours : {lignes.length} dimension
          {lignes.length > 1 ? "s" : ""} renseignée
          {lignes.length > 1 ? "s" : ""}, la contribution n'est pas validée et
          n'entre pas dans le référentiel.
        </p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-muted-foreground">
              <th className="py-2 pr-4 font-medium">Dimension</th>
              <th className="py-2 pr-4 font-medium">Attente</th>
              <th className="py-2 font-medium">Cible du groupe</th>
            </tr>
          </thead>
          <tbody>
            {lignes.map(({ code, cible, stat }) => (
              <tr key={code} className="border-b last:border-0">
                <td className="py-2 pr-4">{libelle(code)}</td>
                <td className="py-2 pr-4">
                  {"nonConcerne" in cible ? (
                    <span className="text-muted-foreground">
                      Pas d'attente particulière
                    </span>
                  ) : (
                    <>
                      {libelleAttentePourCible(cible, echelle)}
                      <span className="ml-2 text-xs tabular-nums text-muted-foreground">
                        {libelleZone(cible)}
                      </span>
                    </>
                  )}
                </td>
                <td className="py-2 tabular-nums">
                  {stat
                    ? `${stat.min.toFixed(1)} – ${stat.max.toFixed(1)}`
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        Échelle {echelle.min}–{echelle.max}. L'attente fixe l'intervalle cible ;
        une dimension sans attente particulière sort du score.
      </p>
    </div>
  );
}
